import type { UseFormReturn } from "react-hook-form";

import { Loader2, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import * as RPNInput from "react-phone-number-input";
import { z } from "zod";

import type { Contract } from "@/types/contract";

import { useCourierOffices, useCouriers } from "@/api/courier-query";
import { useStates } from "@/api/util";
import { usePaymentTransaction } from "@/api/verify-payments";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import useAuthStore from "@/store/auth";
import { PaymentProvider } from "@/types/banks";
import { zodResolver } from "@hookform/resolvers/zod";

import PhoneNumberInput from "../form/phone-input";
import { Checkbox } from "../ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormMessage } from "../ui/form";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { ConfirmPayment } from "./payment-dialog";
import { PickupLocation } from "./pickup-location";
import { ReviewComponent } from "./review";
import { ViewContract } from "./view-contract";

interface OrderReceiptProps {
	contract: Contract;
	open: boolean;
	onOpenChange: (open: boolean) => void;
}
type Steps = "view" | "review" | "phone" | "pickup" | "payment-options" | "confirmation";

export const phoneSchema = z.object({
	phone: z.string({ required_error: "Phone number is required" })
		.min(14, "Phone number is too short")
		.max(14, "Phone number is too long")
		.refine(value => RPNInput.isValidPhoneNumber(value, "NG"), {
			message: "Only NG phone numbers are allowed"
		}),
	default: z.boolean()
});
export const pickupSchema = z.object({
	state: z.string({ required_error: "State is required" }).nonempty("State is required"),
	courierService: z.string({ required_error: "Courier service is required" }).nonempty("Courier service is required"),
	courierBranch: z.string({ required_error: "Courier branch is required" }).nonempty("Courier branch is required"),
	default: z.boolean()
});

const paymentSchema = z.object({
	provider: z.nativeEnum(PaymentProvider)
});

export default function OrderReceipt({ contract, open, onOpenChange }: OrderReceiptProps) {
	const user = useAuthStore(state => state.user);

	// const [open, setOpen] = useState(false);
	// const search = useSearch({ from: "/buyer/home" });
	const [step, setStep] = useState<Steps>("view");

	const courierQuery = useCouriers();
	const states = useStates(user?.countryId ?? "");

	const phoneForm = useForm<z.infer<typeof phoneSchema>>({
		resolver: zodResolver(phoneSchema),
		defaultValues: {
			phone: "",
			default: false
		}
	});
	const pickupForm = useForm<z.infer<typeof pickupSchema>>({
		resolver: zodResolver(pickupSchema),
		defaultValues: {
			state: "",
			courierService: "",
			courierBranch: "",
			default: false
		}
	});

	const paymentForm = useForm<z.infer<typeof paymentSchema>>({
		resolver: zodResolver(paymentSchema),
		defaultValues: {
			provider: undefined
		}
	});
	const courierOffices = useCourierOffices({
		stateId: pickupForm.watch("state"),
		courierId: pickupForm.watch("courierService")
	});

	// console.log("THis sis not running");
	// useEffect(() => {
	// 	if (search.contractId) {
	// 		onOpenChange(true);
	// 		console.log(search.contractId);
	// 	}
	// }, [search.contractId]);
	const handlePhoneSubmit = () => setStep("pickup");

	const tranx = usePaymentTransaction();
	if (!contract)
		return null;

	const total = contract.contractItem.reduce((sum, item) => sum + item.totalCost, 0);

	const handlePickupSubmit = async () => {
		setStep("payment-options");
	};

	const currentCourierBranch = courierOffices.data?.data.data.find(item => Number(pickupForm.getValues("courierBranch")) === item.id);
	const currentState = states.data?.find(item => pickupForm.getValues("state") === item.id);

	const handlePaymentInit = async () => {
		const state = states.data?.find(item => pickupForm.getValues("state") === item.id);

		if (!state || !contract.estimatedWeight)
			throw new Error("State not found");
		tranx.tranxInit.mutateAsync({
			contractId: contract.id,
			paymentOption: paymentForm.getValues("provider"),
			courierPickupOfficeId: pickupForm.getValues("courierBranch"),
			orderAmount: total
		}).then(() => setStep("confirmation"));
	};

	const handlePayment = async () => {
		await tranx.processPayment({
			contractId: contract.id,
			paymentOption: paymentForm.getValues("provider"),
			courierPickupOfficeId: pickupForm.getValues("courierBranch"),
			orderAmount: total + Number(tranx.tranxInit.data?.order?.transactionFee) + Number(tranx.tranxInit.data?.order?.estimatedCourierFee)
		});
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogTrigger asChild>
				<Button>View Contract Details</Button>
			</DialogTrigger>

			<DialogContent className={cn("space-y-2 w-full max-w-md", { "max-w-4xl": step === "review" })}>
				{step === "view" && <ViewContract contract={contract} handleNext={() => setStep("review")} />}
				{step === "review" && (
					<ReviewComponent
						contract={contract}
						handleNext={() => setStep("phone")}
					/>
				)}
				{step === "phone" && (
					<PhoneConfirmation
					// @ts-expect-error - form is not typed
						form={phoneForm}
						handleSubmit={handlePhoneSubmit}
					/>
				)}
				{step === "pickup" && (
					<PickupLocation
					// @ts-expect-error - form is not typed
						form={pickupForm}
						handleSubmit={handlePickupSubmit}
						statesQuery={states}
						courierQuery={courierQuery}
						courierOfficeQuery={courierOffices}
					/>
				)}

				{step === "payment-options" && (

					<>
						<DialogHeader className="flex-row items-start justify-between space-y-0">
							<DialogTitle>Choose a payment method</DialogTitle>
						</DialogHeader>
						<DialogDescription>
							<span className="bg-hover-secondary flex rounded-lg w-full items-center justify-center">
								<TriangleAlert className="size-4 text-primary font-light" />
								<span className="text-center py-4 px-2 text-xs text-primary" role="alert">
									Kindly review the information below before proceeding
								</span>
							</span>
						</DialogDescription>
						<Form {...paymentForm}>
							<form onSubmit={paymentForm.handleSubmit(handlePaymentInit)} className="space-y-4">
								<FormField
									control={paymentForm.control}
									name="provider"
									render={({ field }) => (
										<FormItem>
											<Label htmlFor="paymentOption">Payment Option</Label>
											<FormControl>
												<Select onValueChange={field.onChange} value={field.value}>
													<SelectTrigger id="provider" className="w-full">
														<SelectValue placeholder="Select a payment option" />
													</SelectTrigger>
													<SelectContent>
														<SelectItem value={PaymentProvider.PAYSTACK}>Paystack</SelectItem>
														<SelectItem value={PaymentProvider.SAFEHAVEN}>Safe Haven</SelectItem>
														<SelectItem value={PaymentProvider.WALLET}>Wallet</SelectItem>
														<SelectItem value={PaymentProvider.VFD}>VFD</SelectItem>
													</SelectContent>
												</Select>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<DialogFooter>
									<Button disabled={tranx.tranxInit.isPending} type="submit" className="w-full">
										{tranx.tranxInit.isPending
											? (
													<>
														<Loader2 size={20} className="animate-spin" />
														{" "}
															&nbsp; Loading....
													</>
												)
											: (
													"Continue"
												)}
									</Button>
								</DialogFooter>
							</form>
						</Form>
					</>
				)}
				{step === "confirmation" && (
					<ConfirmPayment
						label="Confirm Payment"
						isPending={tranx.tranxInit.isPending}
						total={total}
						fee={{
							intasida: Number(tranx.tranxInit.data?.order?.transactionFee) ?? 0,
							courier: Number(tranx.tranxInit.data?.order?.estimatedCourierFee) ?? 0
						}}
						handlePayment={handlePayment}
						state={currentState?.name ?? ""}

						courierBranchName={currentCourierBranch?.name ?? ""}
						courierBranchAddress={currentCourierBranch?.address ?? ""}
					/>
				)}
			</DialogContent>

		</Dialog>
	);
}

interface PhoneConfirmationProps {
	handleSubmit: (values: z.infer<typeof phoneSchema>) => void;
	form: UseFormReturn<{
		phone: string;
		default: boolean;
	}, any, undefined>;
}
function PhoneConfirmation({ form, handleSubmit }: PhoneConfirmationProps
) {
	return (
		<>
			<DialogHeader className="flex-row items-start justify-between space-y-0">
				<DialogTitle>Phone Number</DialogTitle>
			</DialogHeader>
			<DialogDescription>
				Add your contact phone number, we need to be able to contact you when your order arrives.
			</DialogDescription>
			<Form {...form}>
				{/* @ts-expect-error - form is not typed */}
				<form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
					<FormField
						// @ts-expect-error - form is not typed
						control={form.control}
						name="phone"
						render={({ field }) => (
							<FormItem>
								<FormControl>
									<PhoneNumberInput {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						// @ts-expect-error - form is not typed
						control={form.control}
						name="default"
						render={({ field }) => (
							<FormItem>
								<FormControl>
									<div className="flex items-center gap-2">
										<Checkbox className="rounded-full" {...field} value={field.value.toString()} />
										<Label htmlFor="phone-number">Set as default phone number</Label>
									</div>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</form>
			</Form>
			<DialogDescription className="text-xs">
				When you set as default number you won't need to enter phone number every time you make an order
			</DialogDescription>
			<DialogFooter>
				{/* @ts-expect-error - form is not typed */}
				<Button disabled={!form.formState.isValid} className="w-full" onClick={form.handleSubmit(handleSubmit)}>Next</Button>
			</DialogFooter>

		</>
	);
}
